/**
 * 브레드크럼 등록표.
 *
 * **새 라우트를 여기 등록하지 않으면 가드가 대시보드로 되돌린다.** 등록을 잊어
 * "버튼을 눌렀는데 대시보드로 튕기는" 증상이 반복됐기 때문에, 조용히 넘기지 않고
 * 개발 중에는 콘솔에 원인을 찍는다 (CLAUDE.md Vue 규약).
 *
 * `labelKey` 는 번역 키다. 화면 제목과 브레드크럼이 같은 키를 쓰므로 한 곳만 고치면 된다.
 */
export interface BreadcrumbEntry {
  labelKey: string
  /** 상위 라우트 이름. 루트면 null. */
  parent: string | null
}

export const breadcrumbs: Record<string, BreadcrumbEntry> = {
  login: { labelKey: 'auth.login_title', parent: null },

  dashboard: { labelKey: 'nav.dashboard', parent: null },
  'my-attendance': { labelKey: 'nav.my_attendance', parent: 'dashboard' },
  'my-summary': { labelKey: 'attendance.my_summary', parent: 'my-attendance' },
  notifications: { labelKey: 'notification.title', parent: 'dashboard' },

  'corrections-mine': { labelKey: 'nav_correction.mine', parent: 'dashboard' },
  'correction-new': { labelKey: 'correction.new_title', parent: 'corrections-mine' },
  'correction-detail': { labelKey: 'correction.detail_title', parent: 'corrections-mine' },
  'correction-inbox': { labelKey: 'nav_correction.inbox', parent: 'dashboard' },

  'team-today': { labelKey: 'nav_more.team', parent: 'dashboard' },
  'team-member': { labelKey: 'team.member_title', parent: 'team-today' },

  'report-period': { labelKey: 'report.period_title', parent: 'dashboard' },
  'report-site': { labelKey: 'report.sites_title', parent: 'dashboard' },
  'report-payroll': { labelKey: 'report.payroll_title', parent: 'dashboard' },
  'report-anomalies': { labelKey: 'anomaly.title', parent: 'dashboard' },
  'report-employee': { labelKey: 'report.employee_title', parent: 'report-period' },
  'export-jobs': { labelKey: 'report.exports_title', parent: 'report-payroll' },

  'admin-entries': { labelKey: 'nav_more.time_entries', parent: 'dashboard' },
  'admin-entry-detail': { labelKey: 'admin_entry.detail_title', parent: 'admin-entries' },
  'admin-employees': { labelKey: 'nav.admin_employees', parent: 'dashboard' },
  'admin-employee-detail': { labelKey: 'employee.detail_title', parent: 'admin-employees' },
  'admin-employee-import': { labelKey: 'employee.import_title', parent: 'admin-employees' },
  'admin-work-sites': { labelKey: 'nav.admin_work_sites', parent: 'dashboard' },
  'admin-work-site-detail': { labelKey: 'work_site.detail_title', parent: 'admin-work-sites' },
  'admin-holidays': { labelKey: 'nav_more.holidays', parent: 'dashboard' },
  'admin-break-waivers': { labelKey: 'nav_more.break_waivers', parent: 'dashboard' },
  'admin-monitored': { labelKey: 'monitored.title', parent: 'dashboard' },
  'admin-gps-failures': { labelKey: 'gps_log.title', parent: 'dashboard' },
  'admin-audit': { labelKey: 'audit.title', parent: 'dashboard' },

  'not-found': { labelKey: 'error.not_found', parent: 'dashboard' },
  forbidden: { labelKey: 'error.forbidden', parent: 'dashboard' },
}

export function isRegistered(name: string): boolean {
  return name in breadcrumbs
}

/** 루트부터 현재까지의 경로. 화면 상단에 그대로 그린다. */
export function trailFor(name: string): Array<{ name: string; labelKey: string }> {
  const trail: Array<{ name: string; labelKey: string }> = []
  let current: string | null = name
  const seen = new Set<string>()

  while (null !== current && !seen.has(current)) {
    const entry: BreadcrumbEntry | undefined = breadcrumbs[current]
    if (undefined === entry) {
      break
    }
    seen.add(current)
    trail.unshift({ name: current, labelKey: entry.labelKey })
    current = entry.parent
  }

  return trail
}
